<template>
    <div
        :class="[$style['mds-search-field'], classObject, sizeClass, variationClass]"
        role="search"
        :data-mds-version="version"
    >
        <input
            type="text"
            :class="$style['mds-search-field__input']"
            :value="value"
            :disabled="disabled"
            :aria-label="label"
            :placeholder="placeholder"
            v-bind="$attrs"
            v-on="inputListeners"
            ref="input"
        />
        <mds-icon name="search" :size="iconSize" :class="$style['mds-search-field__search-icon']"></mds-icon>
        <mds-button
            :class="$style['mds-search-field__clear-button']"
            type="button"
            variation="icon-only"
            :size="iconSize"
            icon="remove"
            :onDark="onDark"
            :text="clearIconAriaLabel"
            @click="clearSearchField($event)"
            :disabled="disableButton"
            v-show="value.length > 0"
        ></mds-button>
        <span :class="$style['mds-search-field__input-focus-outline']"></span>
    </div>
</template>

<script>
    import { MdsButton } from '@mds/button';
    import MdsIcon from '@mds/icon';
    import packageInfo from '../package.json';
    const { name, version } = packageInfo;

    export default {
        name: 'MdsSearchField',
        inheritAttrs: false,
        props: {
            clearIconAriaLabel: {
                type: String,
                default: 'Clear search field',
                docs: {
                    validation: '—',
                    description: 'Sets the `aria-label` on the icon-only clear button.',
                },
            },
            disabled: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, sets the `disabled` attribute.',
                },
            },
            label: {
                type: String,
                default: 'Search',
                docs: {
                    validation: '—',
                    description: 'Sets the `aria-label` for the search field.',
                },
            },
            onDark: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, changes component to on-dark styling.',
                },
            },
            placeholder: {
                type: String,
                default: 'Search...',
                docs: {
                    validation: '—',
                    description: 'Sets the placeholder text for the select.',
                },
            },
            size: {
                type: String,
                default: 'medium',
                validator(value) {
                    return ['small', 'medium', 'large', 'touch'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of: `small`, `medium`, `large`, `touch`',
                    description: 'Sets the size of the search field.',
                },
            },
            value: {
                type: String,
                default: '',
                docs: {
                    validation: '—',
                    description: 'Sets the `value` attribute of the search field',
                    support: ['v-model'],
                },
            },
            variation: {
                type: String,
                default: 'primary',
                validator(value) {
                    return ['primary', 'secondary', 'input'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of `primary`, `secondary`, `input`.',
                    description: 'Set variation',
                },
            },
            version: {
                type: String,
                default: `${name}-${version}`,
            },
        },
        components: {
            MdsButton,
            MdsIcon,
        },
        model: {
            prop: 'value',
        },
        computed: {
            classObject() {
                const obj = {};
                obj[this.$style['mds-search-field--has-value']] = this.value;
                obj[this.$style['mds-component--on-dark']] = this.onDark;

                return obj;
            },
            sizeClass() {
                return this.$style[`mds-search-field--${this.size}`];
            },
            variationClass() {
                return this.$style[`mds-search-field--${this.variation}`];
            },
            iconSize() {
                return this.size === 'small' || this.size === 'medium' ? 'small' : 'medium';
            },
            disableButton() {
                return this.disabled || !this.value;
            },
            inputListeners() {
                var vm = this;
                return Object.assign({}, this.$listeners, {
                    input(event) {
                        vm.$emit('input', event.target.value, event);
                    },
                });
            },
        },
        methods: {
            clearSearchField(event) {
                const input = this.$refs.input;
                input.value = '';
                this.$emit('input', '', event);

                // Custom event if teams wish to listen
                this.$emit('mds-search-field-input-cleared', event);

                // After clearing, bring focus back into the search field
                input.focus();
            },
        },
    };
</script>

<style lang="scss" module>
    @import './search-field.scss';
</style>
